import React, { useState, useEffect } from 'react';
import Icon from '../components/Icon';
import Button, { IconButton, ButtonRow } from '../components/Button';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import workflowService from '../services/workflowService';
import Notification from '../components/Notification';
import ConfirmDialog from '../components/ConfirmDialog';
import useNotification from '../hooks/useNotification';

const WorkflowContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1.5rem;
  flex-wrap: wrap;
  margin-bottom: 1.75rem;
`;

const Title = styled.h1`
  font-family: var(--font-heading);
  font-size: var(--text-3xl);
  font-weight: 700;
  color: var(--text-primary);
  letter-spacing: -0.015em;
  margin: 0 0 0.3rem;
`;

/* Same lede treatment as Leads: what this screen is, in one sentence. */
const Lede = styled.p`
  color: var(--text-secondary);
  font-size: var(--text-base);
  line-height: 1.6;
  margin: 0;
  max-width: 62ch;
`;

/*
 * The one primary action on this screen.
 *
 * It was 1rem/2rem padding at 16px on a gradient with a large shadow, lifting
 * 2px on hover - a marketing hero CTA sitting on a list of campaigns. It is a
 * button now.
 */
const CreateButton = styled(Button).attrs({ $variant: 'primary' })``;

const WorkflowGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  align-items: start;
  gap: 1.5rem;
  margin-top: 2rem;
`;

const WorkflowCard = styled(motion.div)`
  display: flex;
  flex-direction: column;
  background: var(--surface-1);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
  padding: 1.25rem;
  transition: border-color var(--transition-fast), background var(--transition-fast);

  &:hover {
    border-color: var(--border-strong);
    background: var(--surface-2);
  }
`;

const WorkflowHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

const WorkflowName = styled.h3`
  font-family: var(--font-heading);
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
  flex: 1;
`;

const StatusBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: var(--radius-full);
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  
  ${props => {
    switch (props.status) {
      case 'active':
        return `
          background: rgba(52, 211, 153, 0.2);
          color: #34D399;
          border: 1px solid rgba(52, 211, 153, 0.3);
        `;
      case 'paused':
        return `
          background: rgba(251, 191, 36, 0.2);
          color: #fbbf24;
          border: 1px solid rgba(251, 191, 36, 0.3);
        `;
      case 'draft':
        return `
          background: rgba(148, 163, 184, 0.2);
          color: #9ca3af;
          border: 1px solid rgba(148, 163, 184, 0.3);
        `;
      default:
        return `
          background: rgba(148, 163, 184, 0.2);
          color: #9ca3af;
          border: 1px solid rgba(148, 163, 184, 0.3);
        `;
    }
  }}
`;

const WorkflowDescription = styled.p`
  color: var(--text-secondary);
  font-size: 0.9rem;
  line-height: 1.5;
  margin: 0 0 1rem 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

/*
 * Campaign facts as one quiet line.
 *
 * These were four big cyan numbers in bordered tiles, which made step count
 * and success rate the loudest thing on a card whose subject is the campaign.
 * Big-number tiles earn their space when those figures are the point of the
 * screen; here they are footnotes.
 */
const MetaLine = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.3rem 0.6rem;
  color: var(--text-muted);
  font-size: var(--text-xs);
  margin: 0.15rem 0 1rem;

  b {
    color: var(--text-secondary);
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }
`;

const Dot = styled.span`
  color: var(--border-strong);
`;

const WorkflowActions = styled(ButtonRow)`
  padding-top: 0.85rem;
  border-top: 1px solid var(--border-color);
`;


const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: var(--text-muted);

  .icon {
    font-size: 4rem;
    margin-bottom: 1rem;
    opacity: 0.5;
  }

  .title {
    font-size: 1.5rem;
    font-weight: 600;
    color: var(--text-secondary);
    margin-bottom: 0.5rem;
  }

  .description {
    font-size: 1rem;
    line-height: 1.6;
    max-width: 400px;
    margin: 0 auto;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4rem;
  
  .spinner {
    width: 40px;
    height: 40px;
    border: 3px solid var(--border-color);
    border-top: 3px solid var(--accent-cyan);
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const Workflow = () => {
  const navigate = useNavigate();
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { notifications, removeNotification, showError, showInfo } = useNotification();
  // { title, message, confirmLabel, danger, onConfirm } or null
  const [confirmState, setConfirmState] = useState(null);

  useEffect(() => {
    loadWorkflows();
  }, []);

  const loadWorkflows = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await workflowService.getWorkflows();
      setWorkflows(response.data.workflows || []);
    } catch (error) {
      console.error('Error loading workflows:', error);
      setError(error.message || 'Failed to load workflows');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateWorkflow = () => {
    navigate('/workflow/builder');
  };

  const handleEditWorkflow = (workflowId) => {
    navigate(`/workflow/builder/${workflowId}`);
  };

  const handleActivateWorkflow = async (workflowId, event) => {
    event.stopPropagation();
    try {
      await workflowService.activateWorkflow(workflowId);
      await loadWorkflows(); // Refresh the list
    } catch (error) {
      console.error('Error activating workflow:', error);
      setError(error.message || 'Failed to activate workflow');
    }
  };

  const handlePauseWorkflow = async (workflowId, event) => {
    event.stopPropagation();
    try {
      await workflowService.pauseWorkflow(workflowId);
      await loadWorkflows(); // Refresh the list
    } catch (error) {
      console.error('Error pausing workflow:', error);
      setError(error.message || 'Failed to pause workflow');
    }
  };

  const startExecution = async (workflowId) => {
    try {
      // Show immediate notification that workflow has started
      showInfo(
        'Workflow execution has been initiated. Please wait for completion.',
        'Workflow Started'
      );
      
      await workflowService.executeWorkflow(workflowId);
      
      // Refresh workflows to update execution stats (no completion notification)
      await loadWorkflows();
    } catch (error) {
      console.error('Error executing workflow:', error);
      showError(
        error.message || 'Failed to execute workflow. Please try again.',
        'Execution Failed'
      );
    }
  };

  const handleExecuteWorkflow = async (workflowId, event) => {
    event.stopPropagation();
    // Time matters to the person clicking; infrastructure cost does not, so
    // the dialog mentions lead count and duration only. If the estimate call
    // fails it must never block the run.
    let message = 'Start this campaign run now?';
    try {
      const est = await workflowService.getRunEstimate(workflowId);
      const mins = est.minutes < 1 ? 'under a minute' : `about ${est.minutes} min`;
      const leads = `${est.leadCount} lead${est.leadCount === 1 ? '' : 's'}`;

      // Only a workflow that drafts can promise drafts. Saying "drafts will
      // wait in Approvals" on a sourcing-only run described something that
      // never happens, and left the operator waiting for it.
      message = est.drafts === false
        ? `This will find ${leads} and add them to Leads (${mins}). No messages are drafted or sent.`
        : `This will research ${leads} (${mins}). Drafts will wait in Approvals - nothing is sent automatically.`;
    } catch (estimateError) {
      console.warn('Estimate unavailable:', estimateError);
    }

    setConfirmState({
      title: 'Run campaign?',
      message,
      confirmLabel: 'Start run',
      danger: false,
      onConfirm: () => { setConfirmState(null); startExecution(workflowId); }
    });
  };

  const deleteWorkflow = async (workflowId) => {
    try {
      await workflowService.deleteWorkflow(workflowId);
      await loadWorkflows(); // Refresh the list
    } catch (error) {
      console.error('Error deleting workflow:', error);
      setError(error.message || 'Failed to delete workflow');
    }
  };

  const handleDeleteWorkflow = (workflowId, event) => {
    event.stopPropagation();
    setConfirmState({
      title: 'Delete workflow?',
      message: 'This removes the workflow and stops its schedule. Past executions stay in history.',
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: () => { setConfirmState(null); deleteWorkflow(workflowId); }
    });
  };

  const handleViewExecutions = (workflowId, event) => {
    event.stopPropagation();
    navigate(`/workflow/${workflowId}/executions`);
  };

  // Targeting lives on the campaign now, so it is edited from the campaign
  // rather than from a project-wide page two clicks away.
  const handleEditAudience = (workflowId, e) => {
    e.stopPropagation();
    navigate(`/icp-selection?campaign=${workflowId}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
  return (
      <WorkflowContainer>
        <LoadingSpinner>
          <div className="spinner"></div>
        </LoadingSpinner>
      </WorkflowContainer>
    );
  }

  return (
    <>
      <WorkflowContainer>
        <Header>
          <div>
            <Title>Workflows</Title>
            <Lede>
              A workflow is the recipe a campaign runs on: who to find, what to
              research, and what to write. Nothing sends until you approve it.
            </Lede>
          </div>
          <CreateButton onClick={handleCreateWorkflow}>
            <Icon name="plus" size={16} />
            Build a workflow
          </CreateButton>
        </Header>

      {error && (
        <div style={{ 
          background: 'rgba(248, 113, 113, 0.1)', 
          border: '1px solid rgba(248, 113, 113, 0.3)',
          color: '#F87171',
          padding: '1rem',
          borderRadius: 'var(--radius-md)',
          marginBottom: '1rem'
        }}>
          {error}
        </div>
      )}

      {workflows.length === 0 ? (
        <EmptyState>
          <div className="icon"><Icon name="workflow" size={26} /></div>
          <div className="title">No workflows yet</div>
          <div className="description">
            Create your first workflow to automate your sales processes. 
            Start with a simple schedule node to get familiar with the workflow builder.
          </div>
        </EmptyState>
      ) : (
        <WorkflowGrid>
          <AnimatePresence>
            {workflows.map((workflow) => (
              <WorkflowCard
                key={workflow.id}
                onClick={() => handleEditWorkflow(workflow.id)}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.2 }}
              >
                <WorkflowHeader>
                  <WorkflowName>{workflow.name}</WorkflowName>
                  <StatusBadge status={workflow.status}>
                    {workflow.status}
                  </StatusBadge>
                </WorkflowHeader>

                {workflow.description && (
                  <WorkflowDescription>
                    {workflow.description}
                  </WorkflowDescription>
                )}

                <MetaLine>
                  <span><b>{workflow.nodeCount}</b> step{workflow.nodeCount === 1 ? '' : 's'}</span>
                  <Dot>·</Dot>
                  <span><b>{workflow.totalExecutions}</b> run{workflow.totalExecutions === 1 ? '' : 's'}</span>
                  {workflow.totalExecutions > 0 && (
                    <>
                      <Dot>·</Dot>
                      <span><b>{workflow.successRate}%</b> succeeded</span>
                    </>
                  )}
                  <Dot>·</Dot>
                  <span>{workflow.totalExecutions > 0 && workflow.lastExecutedAt
                    ? `last run ${formatDate(workflow.lastExecutedAt)}`
                    : 'never run'}</span>
                </MetaLine>

                <WorkflowActions>
                  {/* One primary per card: the thing you most likely came to do. */}
                  {workflow.status === 'draft' || workflow.status === 'paused' ? (
                    <Button $variant="primary" onClick={(e) => handleActivateWorkflow(workflow.id, e)}>
                      <Icon name="play" size={14} /> Activate
                    </Button>
                  ) : (
                    <Button onClick={(e) => handlePauseWorkflow(workflow.id, e)}>
                      <Icon name="pause" size={14} /> Pause
                    </Button>
                  )}

                  <Button onClick={(e) => handleExecuteWorkflow(workflow.id, e)}>Run now</Button>
                  <Button $variant="ghost" onClick={(e) => handleEditAudience(workflow.id, e)}>Audience</Button>
                  <Button $variant="ghost" onClick={(e) => handleViewExecutions(workflow.id, e)}>Runs</Button>

                  {/* Destructive, so it is reachable but never the thing your
                      eye lands on, and never beside the button you actually want. */}
                  <IconButton
                    $variant="danger"
                    style={{ marginLeft: 'auto' }}
                    aria-label={`Delete ${workflow.name}`}
                    title="Delete this campaign"
                    onClick={(e) => handleDeleteWorkflow(workflow.id, e)}
                  >
                    <Icon name="trash" size={15} />
                  </IconButton>
                </WorkflowActions>
              </WorkflowCard>
            ))}
          </AnimatePresence>
        </WorkflowGrid>
      )}
      </WorkflowContainer>
      
      <Notification 
        notifications={notifications} 
        removeNotification={removeNotification} 
      />
      <ConfirmDialog
        open={Boolean(confirmState)}
        title={confirmState?.title}
        message={confirmState?.message}
        confirmLabel={confirmState?.confirmLabel}
        danger={confirmState?.danger}
        onConfirm={confirmState?.onConfirm}
        onCancel={() => setConfirmState(null)}
      />
    </>
  );
};

export default Workflow;
